import BlogLayout from '@/pages/blog/components/BlogLayout';
import Breadcrumbs from '@/pages/blog/components/Breadcrumbs';
import TableOfContents from '@/pages/blog/components/TableOfContents';
import AuthorBio from '@/pages/blog/components/AuthorBio';
import RelatedArticles from '@/pages/blog/components/RelatedArticles';

const tocItems = [
  { id: 'intro', label: 'Shoulder Health After 60: What Changes' },
  { id: 'rotator-cuff', label: 'Understanding the Rotator Cuff' },
  { id: 'best-exercises', label: 'Best Shoulder Exercises for Men Over 60' },
  { id: 'rotator-cuff-exercises', label: 'Rotator Cuff Strengthening Exercises' },
  { id: 'exercises-avoid', label: 'Shoulder Exercises to Avoid or Modify' },
  { id: 'warm-up', label: 'The Shoulder Warm-Up Protocol' },
  { id: 'sample-workout', label: 'A Complete Shoulder Workout' },
  { id: 'faq', label: 'Frequently Asked Questions' },
];

const relatedArticles = [
  { slug: 'joint-pain-exercises-for-seniors', title: 'Exercise WITH Joint Pain: Safe Movements for Men Over 60', category: 'Health & Recovery' },
  { slug: 'back-exercises-for-men-over-60', title: 'Back Strengthening Exercises for Men Over 60', category: 'Health & Recovery' },
  { slug: 'weight-training-over-60-safety-tips', title: 'Weight Training Over 60: 10 Safety Tips Every Man Needs to Know', category: 'Strength Training' },
];

const schema = JSON.stringify([
  {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Shoulder Exercises for Men Over 60 (Rotator Cuff Safe)',
    author: { '@type': 'Person', name: 'Mason Hale', url: 'https://masonhalefit.com' },
    publisher: { '@type': 'Organization', name: 'Mason Hale Fitness', url: 'https://masonhalefit.com' },
    datePublished: '2024-03-15',
    dateModified: '2024-04-06',
    description: 'The best shoulder exercises for men over 60 that protect the rotator cuff. Build strong, healthy shoulders without injury using these safe movements.',
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://masonhalefit.com/blog/shoulder-exercises-for-men-over-60' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What shoulder exercises are safe for men over 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'Safe shoulder exercises for men over 60 include: dumbbell lateral raises (light weight), front raises, seated dumbbell press (not behind the neck), face pulls with resistance bands, and rotator cuff strengthening exercises. Avoid behind-the-neck pressing and upright rows, which stress the rotator cuff.' },
      },
      {
        '@type': 'Question',
        name: 'How do I strengthen my rotator cuff after 60?',
        acceptedAnswer: { '@type': 'Answer', text: 'Rotator cuff strengthening exercises include: external rotation with resistance band, internal rotation with resistance band, side-lying external rotation with light dumbbell, and prone Y-T-W exercises. These should be done with very light resistance and high reps (15-20) to strengthen the small rotator cuff muscles.' },
      },
      {
        '@type': 'Question',
        name: 'Can I do overhead pressing with shoulder pain?',
        acceptedAnswer: { '@type': 'Answer', text: 'It depends on the cause of the pain. Many men can do overhead pressing with modifications: using dumbbells instead of a barbell (allows natural wrist rotation), pressing to ear level rather than full overhead, or switching to a landmine press which reduces shoulder impingement risk.' },
      },
      {
        '@type': 'Question',
        name: 'How do I prevent rotator cuff injuries when lifting?',
        acceptedAnswer: { '@type': 'Answer', text: 'Prevent rotator cuff injuries by: warming up thoroughly, avoiding behind-the-neck exercises, not going too heavy on overhead pressing, including rotator cuff strengthening exercises in your routine, and maintaining balanced training between pushing and pulling movements.' },
      },
    ],
  },
]);

export default function ShoulderExercisesPage() {
  return (
    <BlogLayout
      title="Shoulder Exercises for Men Over 60 (Rotator Cuff Safe)"
      description="The best shoulder exercises for men over 60 that protect the rotator cuff. Build strong, healthy shoulders without injury using these safe movements."
      keywords="shoulder exercises for men over 60, rotator cuff safe exercises, shoulder workout over 60, shoulder exercises seniors"
      canonicalPath="/blog/shoulder-exercises-for-men-over-60"
      datePublished="2024-03-15"
      dateModified="2024-04-06"
      schemaMarkup={schema}
    >
      <Breadcrumbs title="Shoulder Exercises for Men Over 60 (Rotator Cuff Safe)" />

      <div className="mb-6">
        <span className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase">Health &amp; Recovery</span>
      </div>

      <h1 className="text-white text-3xl sm:text-4xl font-black leading-tight mb-4">
        Shoulder Exercises for Men Over 60 (Rotator Cuff Safe)
      </h1>

      <p className="text-gray-400 text-sm mb-8">
        By <strong className="text-white">Mason Hale</strong> &nbsp;·&nbsp; March 15, 2024 &nbsp;·&nbsp; 9 min read
      </p>

      <TableOfContents items={tocItems} />

      <section id="intro" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Shoulder Health After 60: What Changes</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The shoulder is the most mobile joint in the human body — and that mobility comes at a cost. After 60, the rotator cuff tendons thin, the cartilage wears, and the risk of impingement and tears increases significantly.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          But here's the thing: strong shoulders are essential for quality of life after 60. Reaching overhead, carrying groceries, getting dressed — all of these require functional shoulder strength. And the best way to protect aging shoulders is to strengthen them correctly.
        </p>
        <p className="text-gray-300 leading-relaxed">
          This guide covers the best shoulder exercises for men over 60 — movements that build real strength while protecting the rotator cuff.
        </p>
      </section>

      <section id="rotator-cuff" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Understanding the Rotator Cuff</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          The rotator cuff is a group of four small muscles that stabilize the shoulder joint. They're not the big, visible muscles — they're the deep stabilizers that keep the ball of the shoulder in the socket during movement.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          The <a href="https://www.mayoclinic.org/diseases-conditions/rotator-cuff-injury/symptoms-causes/syc-20350225" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">Mayo Clinic</a> notes that rotator cuff tears are extremely common in men over 60 — many men have partial tears without even knowing it. The key is strengthening these muscles before problems develop, and training around them if they're already compromised.
        </p>
        <p className="text-gray-300 leading-relaxed">
          The four rotator cuff muscles are: supraspinatus, infraspinatus, teres minor, and subscapularis. You don't need to memorize these — you just need to know that specific exercises target them, and those exercises should be part of every older man's training.
        </p>
      </section>

      <section id="best-exercises" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Best Shoulder Exercises for Men Over 60</h2>
        <div className="space-y-4 mb-4">
          {[
            { name: 'Seated Dumbbell Press', sets: '3 × 10-12', desc: 'Press dumbbells from shoulder height to ear level (not full overhead). Seated position provides stability. Dumbbells allow natural wrist rotation, reducing impingement risk versus barbell.' },
            { name: 'Dumbbell Lateral Raise', sets: '3 × 12-15', desc: 'Raise dumbbells to shoulder height with slight elbow bend. Use light weight — this exercise is more effective with less weight than you think. Builds the medial deltoid.' },
            { name: 'Front Raise', sets: '3 × 12', desc: 'Raise one dumbbell at a time to shoulder height. Builds the anterior deltoid. Alternate arms to reduce fatigue.' },
            { name: 'Face Pull (resistance band)', sets: '3 × 15-20', desc: 'Anchor band at face height, pull toward face with elbows high. One of the best exercises for shoulder health — strengthens the rear deltoid and rotator cuff simultaneously.' },
            { name: 'Bent-Over Rear Delt Raise', sets: '3 × 12-15', desc: 'Hinge forward, raise dumbbells out to sides. Builds the posterior deltoid, which is chronically weak in most men and critical for shoulder balance.' },
            { name: 'Landmine Press', sets: '3 × 10 each arm', desc: 'Press a barbell anchored at floor level at a 45-degree angle. Significantly reduces shoulder impingement risk compared to overhead pressing.' },
          ].map((item) => (
            <div key={item.name} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <div className="flex items-center justify-between mb-2">
                <strong className="text-white">{item.name}</strong>
                <span className="text-[#c8a96e] text-xs font-bold">{item.sets}</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="rotator-cuff-exercises" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Rotator Cuff Strengthening Exercises</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          These exercises specifically target the rotator cuff muscles. Use very light resistance — 2-5 lbs or a light resistance band. These are not ego exercises. They're injury prevention.
        </p>
        <ul className="space-y-3 mb-4">
          {[
            { name: 'External rotation (band)', desc: 'Elbow at 90 degrees, rotate forearm outward against band resistance. 3 × 15-20 each arm.' },
            { name: 'Internal rotation (band)', desc: 'Elbow at 90 degrees, rotate forearm inward against band resistance. 3 × 15-20 each arm.' },
            { name: 'Side-lying external rotation', desc: 'Lying on side, rotate light dumbbell upward. Directly targets the infraspinatus. 3 × 15 each side.' },
            { name: 'Prone Y-T-W', desc: 'Lying face down, raise arms in Y, T, and W positions. Strengthens the entire rotator cuff and rear shoulder. 2 × 10 each position.' },
          ].map((item) => (
            <li key={item.name} className="flex items-start gap-3">
              <span className="text-[#c8a96e] mt-1 flex-shrink-0"><i className="ri-arrow-right-s-line" /></span>
              <div>
                <strong className="text-white">{item.name}:</strong>
                <span className="text-gray-400 text-sm ml-1">{item.desc}</span>
              </div>
            </li>
          ))}
        </ul>
        <p className="text-gray-300 leading-relaxed">
          The <a href="https://www.nia.nih.gov/health/exercise-physical-activity/four-types-exercise-can-improve-your-health-and-physical-ability" target="_blank" rel="noopener noreferrer" className="text-[#c8a96e] hover:text-[#d9bc82] underline">National Institute on Aging</a> recommends including strength exercises for all major muscle groups — including the often-neglected rotator cuff muscles.
        </p>
      </section>

      <section id="exercises-avoid" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">Shoulder Exercises to Avoid or Modify</h2>
        <ul className="space-y-2 mb-4">
          {[
            { exercise: 'Behind-the-neck press', reason: 'Places the shoulder in extreme external rotation under load. High injury risk for men over 60.' },
            { exercise: 'Behind-the-neck lat pulldown', reason: 'Same issue — extreme shoulder position. Use front pulldowns instead.' },
            { exercise: 'Upright rows', reason: 'Causes shoulder impingement in many men. Replace with face pulls.' },
            { exercise: 'Full overhead press with barbell', reason: 'Modify to dumbbell press or landmine press to reduce impingement risk.' },
          ].map((item) => (
            <li key={item.exercise} className="flex items-start gap-3">
              <span className="text-red-400 mt-1 flex-shrink-0"><i className="ri-close-circle-line" /></span>
              <div>
                <strong className="text-white">{item.exercise}:</strong>
                <span className="text-gray-400 text-sm ml-1">{item.reason}</span>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section id="warm-up" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">The Shoulder Warm-Up Protocol</h2>
        <p className="text-gray-300 leading-relaxed mb-4">
          Never skip the shoulder warm-up. Cold shoulder joints are vulnerable. Spend 5-8 minutes on this before any shoulder training:
        </p>
        <ol className="space-y-2 mb-4">
          {[
            'Arm circles — 10 small, 10 medium, 10 large in each direction',
            'Cross-body arm swings — 20 reps, loosens the posterior capsule',
            'Wall slides — back against wall, slide arms overhead and back down, 10 reps',
            'Band pull-aparts — 20 reps with light band, activates rear deltoid',
            'Light lateral raises — 15 reps with very light weight to warm up the joint',
          ].map((step, i) => (
            <li key={step} className="flex items-start gap-3">
              <span className="text-[#c8a96e] font-bold flex-shrink-0 w-5">{i + 1}.</span>
              <span className="text-gray-300 text-sm">{step}</span>
            </li>
          ))}
        </ol>
      </section>

      <section id="sample-workout" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-4">A Complete Shoulder Workout</h2>
        <div className="bg-[#111111] border border-white/10 rounded-lg p-5 mb-4">
          <p className="text-[#c8a96e] text-xs font-bold tracking-[0.2em] uppercase mb-4">Shoulder Day (30-35 minutes)</p>
          <div className="space-y-2">
            {[
              { exercise: 'Warm-up protocol', sets: '5-8 min' },
              { exercise: 'Seated Dumbbell Press', sets: '3 × 10-12' },
              { exercise: 'Lateral Raise', sets: '3 × 12-15' },
              { exercise: 'Face Pull (band)', sets: '3 × 15-20' },
              { exercise: 'Bent-Over Rear Delt Raise', sets: '3 × 12' },
              { exercise: 'External Rotation (band)', sets: '2 × 15 each' },
              { exercise: 'Prone Y-T-W', sets: '2 × 10 each' },
            ].map((item) => (
              <div key={item.exercise} className="flex items-center justify-between py-1.5 border-b border-white/5 last:border-0">
                <span className="text-gray-300 text-sm">{item.exercise}</span>
                <span className="text-[#c8a96e] text-sm font-bold">{item.sets}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-gray-300 leading-relaxed">
          For a complete program that integrates shoulder training with full-body strength work, the <a href="https://masonhalefit.com" className="text-[#c8a96e] hover:text-[#d9bc82] underline font-semibold">Over-60 Strength Blueprint</a> provides the full system — every exercise chosen for joint safety.
        </p>
      </section>

      <section id="faq" className="mb-10">
        <h2 className="text-white text-2xl font-black mb-6">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {[
            { q: 'What shoulder exercises are safe for men over 60?', a: 'Safe options include: dumbbell lateral raises, front raises, seated dumbbell press, face pulls with resistance bands, and rotator cuff strengthening exercises. Avoid behind-the-neck pressing and upright rows.' },
            { q: 'How do I strengthen my rotator cuff after 60?', a: 'Use external rotation with resistance band, internal rotation with resistance band, side-lying external rotation with light dumbbell, and prone Y-T-W exercises. Use very light resistance and high reps (15-20).' },
            { q: 'Can I do overhead pressing with shoulder pain?', a: 'It depends on the cause. Try dumbbells instead of a barbell, press to ear level rather than full overhead, or switch to a landmine press which reduces shoulder impingement risk.' },
            { q: 'How do I prevent rotator cuff injuries when lifting?', a: 'Warm up thoroughly, avoid behind-the-neck exercises, don\'t go too heavy on overhead pressing, include rotator cuff strengthening exercises, and maintain balanced training between pushing and pulling movements.' },
          ].map((item) => (
            <div key={item.q} className="bg-[#111111] border border-white/10 rounded-lg p-5">
              <p className="text-white font-bold mb-2">{item.q}</p>
              <p className="text-gray-400 text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-[#c8a96e]/10 border border-[#c8a96e]/30 rounded-lg p-6 mb-10 text-center">
        <p className="text-[#c8a96e] font-black text-lg mb-2">Train Your Shoulders the Smart Way</p>
        <p className="text-gray-300 text-sm mb-4">The Over-60 Strength Blueprint includes a complete shoulder training protocol built around rotator cuff safety. Every exercise is chosen to build strength without injury risk.</p>
        <a href="https://masonhalefit.com" className="inline-block bg-[#c8a96e] text-black font-black text-sm tracking-[0.15em] uppercase px-8 py-3 rounded-sm hover:bg-[#d9bc82] transition-all cursor-pointer whitespace-nowrap">
          Get The Blueprint — $19.99
        </a>
      </div>

      <AuthorBio />
      <RelatedArticles articles={relatedArticles} />
    </BlogLayout>
  );
}
